// src/services/controlPanelApi.js
import axios from 'axios';
import { getJwtToken } from '../utils/auth';
import {getBaseUrl} from "../utils/apiClient";

const controlPanelApiClient = axios.create({
    baseURL: `${getBaseUrl()}/panel`,
});

controlPanelApiClient.interceptors.request.use(async (config) => {
    const token = await getJwtToken(); // Espera a que se obtenga el token
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    //config.headers['bypass-tunnel-reminder'] = 'true';
    return config;
}, (error) => {
    return Promise.reject(error); // Si hay un error, lo propagamos
});

export const authenticate = async (password) => {
    try {
        const response = await controlPanelApiClient.post('/authenticate', { password });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getSuggestions = async () => {
    try {
        const response = await controlPanelApiClient.get('/suggestions');
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const getUnseenSuggestions = async () => {
    try {
        const response = await controlPanelApiClient.get('/suggestions/unseen');
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const setSuggestionSeen = async (suggestionId) => {
    try {
        const response = await controlPanelApiClient.put(`/suggestions/${suggestionId}/seen`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const setSuggestionUnseen = async (suggestionId) => {
    try {
        const response = await controlPanelApiClient.put(`/suggestions/${suggestionId}/unseen`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const deleteSuggestion = async (suggestionId) => {
    try {
        const response = await controlPanelApiClient.delete(`/suggestions/${suggestionId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const suggest = async (suggestion) => {
    try {
        const response = await controlPanelApiClient.post('/suggestions', suggestion);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const countSuggestions = async () => {
    try {
        const response = await controlPanelApiClient.get('/suggestions/count');
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Posts del foro (admin): trae todos los posts ordenados por fecha (más nuevos primero)
export const getAllForumPosts = async () => {
    try {
        const response = await controlPanelApiClient.get('/posts');
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Proveedores interesados en un post (admin)
export const getForumPostInterests = async (postId) => {
    try {
        const response = await controlPanelApiClient.get(`/posts/${postId}/interests`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Elimina definitivamente un post del foro (admin)
export const deleteForumPost = async (postId) => {
    try {
        const response = await controlPanelApiClient.delete(`/posts/${postId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Nuevos endpoints para manejo de proveedores
// Service Providers
export const createServiceProvider = async (providerData) => {
    try {
        const response = await controlPanelApiClient.post('/providers/service', providerData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const updateServiceProvider = async (username, providerData) => {
    try {
        const response = await controlPanelApiClient.put(`/providers/service/${username}`, providerData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getServiceProvider = async (username) => {
    try {
        const response = await controlPanelApiClient.get(`/providers/service/${username}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Función eliminada - usar deleteProvider de providersApi.js

// Products Providers
export const createProductsProvider = async (providerData) => {
    try {
        const response = await controlPanelApiClient.post('/providers/products', providerData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const updateProductsProvider = async (username, providerData) => {
    try {
        const response = await controlPanelApiClient.put(`/providers/products/${username}`, providerData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getProductsProvider = async (username) => {
    try {
        const response = await controlPanelApiClient.get(`/providers/products/${username}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Función eliminada - usar deleteProvider de providersApi.js

// New Notification APIs
export const sendNotificationToAllUsers = async (title, body, type, redirectPath) => {
    try {
        const payload = { title, body, type };
        if (redirectPath) payload.redirectPath = redirectPath;
        const response = await controlPanelApiClient.post('/notify/all', payload);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const sendNotificationToProviders = async (title, body, type, redirectPath) => {
    try {
        const payload = { title, body, type };
        if (redirectPath) payload.redirectPath = redirectPath;
        const response = await controlPanelApiClient.post('/notify/providers', payload);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const sendNotificationToProvidersByType = async (providerType, title, body, type, redirectPath) => {
    try {
        const payload = { title, body, type };
        if (redirectPath) payload.redirectPath = redirectPath;
        const response = await controlPanelApiClient.post(`/notify/providers/${providerType}`, payload);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Email sending APIs
// Body: { subject: string, message: string, buttonText?: string, buttonUrl?: string }
export const sendEmailToAllUsers = async ({ subject, message, buttonText, buttonUrl }) => {
    try {
        const response = await controlPanelApiClient.post('/email/all', {
            subject,
            message,
            buttonText,
            buttonUrl,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const sendEmailToProviders = async ({ subject, message, buttonText, buttonUrl }) => {
    try {
        const response = await controlPanelApiClient.post('/email/providers', {
            subject,
            message,
            buttonText,
            buttonUrl,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const sendEmailToNonProviders = async ({ subject, message, buttonText, buttonUrl }) => {
    try {
        const response = await controlPanelApiClient.post('/email/non-providers', {
            subject,
            message,
            buttonText,
            buttonUrl,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Send email to a specific user by email address
// Body: { email: string, subject: string, message: string, buttonText?: string, buttonUrl?: string }
export const sendEmailToUser = async ({ email, subject, message, buttonText, buttonUrl }) => {
    try {
        const response = await controlPanelApiClient.post('/email/user', {
            email,
            subject,
            message,
            buttonText,
            buttonUrl,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Send email to a list of users (emails separated by comma/semicolon/space)
// Body: { emails: string, subject: string, message: string, buttonText?: string, buttonUrl?: string }
export const sendEmailToUsersList = async ({ emails, subject, message, buttonText, buttonUrl }) => {
    try {
        const response = await controlPanelApiClient.post('/email/users-list', {
            emails,
            subject,
            message,
            buttonText,
            buttonUrl,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};
